const pad2 = (value) => String(value).padStart(2, '0');

export const PARTNER_DATE_INPUT_PLACEHOLDER = 'MM/DD/YYYY';

export const parsePartnerDateParts = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const str = String(value).trim();
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      year: isoMatch[1],
      month: isoMatch[2],
      day: isoMatch[3],
    };
  }

  const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    return {
      year: slashMatch[3],
      month: pad2(slashMatch[1]),
      day: pad2(slashMatch[2]),
    };
  }

  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return {
    year: String(parsed.getFullYear()),
    month: pad2(parsed.getMonth() + 1),
    day: pad2(parsed.getDate()),
  };
};

const formatPartnerDateParts = (parts) => `${parts.month}/${parts.day}/${parts.year}`;

export const toPartnerIsoDate = (value) => {
  const parts = parsePartnerDateParts(value);
  if (!parts) {
    return '';
  }

  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const formatPartnerDateForInput = (value) => {
  if (!value) {
    return '';
  }

  const parts = parsePartnerDateParts(value);
  if (!parts) {
    return '';
  }

  return formatPartnerDateParts(parts);
};

export const isValidPartnerDateInput = (value) => {
  if (!value || !String(value).trim()) {
    return false;
  }

  const parts = parsePartnerDateParts(value);
  if (!parts) {
    return false;
  }

  const month = Number(parts.month);
  const day = Number(parts.day);
  const year = Number(parts.year);

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) {
    return false;
  }

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day
  );
};

export const getPartnerDateInputError = (value) => {
  if (!value || !String(value).trim()) {
    return 'This field is required';
  }

  if (!isValidPartnerDateInput(value)) {
    return 'Enter a valid date as MM/DD/YYYY';
  }

  return '';
};

const formatDatePartsForDisplay = (parts) => {
  const date = new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatParsedDateTime = (parsed, separator) => {
  const datePart = formatDatePartsForDisplay({
    year: String(parsed.getFullYear()),
    month: pad2(parsed.getMonth() + 1),
    day: pad2(parsed.getDate()),
  });
  if (!datePart) {
    return null;
  }

  const timePart = parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${datePart}${separator}${timePart}`;
};

export const formatPartnerDate = (value) => {
  if (!value) {
    return '-';
  }

  const parts = parsePartnerDateParts(value);
  if (!parts) {
    return String(value);
  }

  const formatted = formatDatePartsForDisplay(parts);
  return formatted || String(value);
};

export const formatPartnerDisplayDate = formatPartnerDate;

export const formatPartnerTableDate = (value) => {
  if (!value) {
    return '-';
  }

  const parts = parsePartnerDateParts(value);
  if (!parts) {
    return String(value);
  }

  const date = new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatPartnerDisplayDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return formatParsedDateTime(parsed, ', ') || String(value);
};

export const formatPartnerActivityDateTime = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return formatParsedDateTime(parsed, ' · ') || String(value);
};

export const formatPartnerDateTime = formatPartnerDisplayDateTime;
